import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import { Storefront } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import { UpgradePlansModal } from './UpgradePlansModal';

interface PriceZone {
  zoneId: string;
  kind: 'market' | 'retail_location';
  name: string;
  currency?: string | null;
  priceListId?: string | null;
  priceListReason?: 'no_market' | 'market_has_no_price_list' | 'market_shared';
}

const PRICE_LIST_REASON_KEY: Record<'no_market' | 'market_has_no_price_list' | 'market_shared', string> = {
  no_market: 'noMarket',
  market_has_no_price_list: 'noPriceList',
  market_shared: 'marketShared',
};

interface ZonesResponse {
  zones: PriceZone[];
  scopeStatus?: 'reconnect_required';
  missing?: string[];
  mapped: Array<{ locationId: string; locationName: string; zoneId: string; kind: PriceZone['kind'] }>;
}

interface StoreLocationRow {
  id: string;
  name: string;
  integrationIds: string[];
  platformZones?: Array<{ integrationId: string; zoneId: string; kind: PriceZone['kind'] }>;
}

export interface ShopifyPricingZonesSectionProps {
  integrationId: string;
  /** Plan of the store, for the upgrade modal when custom pricing is not included. */
  currentPlanId?: string;
}

/**
 * One Shopify account with POS at several stores is one integration, so the
 * sync writes one price per product. Mapping each store location to a Shopify
 * market (or retail location) lets that location's own price sheets price its
 * zone. Per-zone pricing is part of custom pricing, so a store whose plan does
 * not include it sees the upgrade modal instead of a saved mapping.
 */
const ShopifyPricingZonesSection: React.FC<ShopifyPricingZonesSectionProps> = ({ integrationId, currentPlanId }) => {
  const { t } = useAppTranslation();
  const queryClient = useQueryClient();
  const { enabled } = useOrgFeatureFlag('shopify_markets_pricing');
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchZones = async (refresh: boolean): Promise<ZonesResponse | null> => {
    const response = await makeApiCall(
      `/integrations/${integrationId}/shopify/zones${refresh ? '?refresh=true' : ''}`,
      { headers: await getAuthHeaders() },
    );
    if (!response.ok) return null;
    return ((await response.json()).data || null) as ZonesResponse | null;
  };

  const zones = useQuery({
    queryKey: ['shopify-price-zones', integrationId],
    enabled,
    queryFn: () => fetchZones(false),
  });

  // The refresh button means "re-read Shopify", not "re-read the cache the
  // route otherwise serves" — a plain refetch would hit the same
  // `refresh=false` route and never see a market added since the last read.
  const refreshFromShopify = useMutation({
    mutationFn: () => fetchZones(true),
    onSuccess: (data) => {
      queryClient.setQueryData(['shopify-price-zones', integrationId], data);
    },
  });

  const locations = useQuery({
    queryKey: ['store-locations'],
    enabled,
    queryFn: async (): Promise<StoreLocationRow[]> => {
      const response = await makeApiCall('/store/locations', { headers: await getAuthHeaders() });
      if (!response.ok) return [];
      return ((await response.json()).data || []) as StoreLocationRow[];
    },
  });

  const served = useMemo(
    () => (locations.data || []).filter((l) => (l.integrationIds || []).includes(integrationId)),
    [locations.data, integrationId],
  );

  const save = useMutation({
    mutationFn: async (input: { locationId: string; zoneId: string | null; kind: PriceZone['kind'] }) => {
      const response = await makeApiCall(`/store/locations/${input.locationId}/platform-zone`, {
        method: 'PUT',
        headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
        body: JSON.stringify({ integrationId, zoneId: input.zoneId, kind: input.kind }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        const err = new Error(body?.message || body?.error || t('integrations.pricingZones.saveFailed'));
        (err as Error & { status?: number }).status = response.status;
        throw err;
      }
      return (await response.json()).data as StoreLocationRow;
    },
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['store-locations'] });
      queryClient.invalidateQueries({ queryKey: ['shopify-price-zones', integrationId] });
    },
    onError: (err: Error & { status?: number }) => {
      // Custom pricing is a plan entitlement: offer the upgrade rather than an error.
      if (err.status === 403) setUpgradeOpen(true);
      else setError(err.message);
    },
  });

  if (!enabled) return null;

  const needsReconnect = zones.data?.scopeStatus === 'reconnect_required';
  const available = zones.data?.zones || [];

  const zoneFor = (location: StoreLocationRow): string =>
    (location.platformZones || []).find((z) => z.integrationId === integrationId)?.zoneId || '';

  return (
    <Paper variant="outlined">
      <Box sx={{ px: 2, py: 1.25, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Storefront fontSize="small" color="action" />
          <Typography variant="subtitle2">{t('integrations.pricingZones.title')}</Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.45 }}>
          {t('integrations.pricingZones.subtitle')}
        </Typography>
      </Box>
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {needsReconnect && (
          <Alert severity="warning">
            {t('integrations.pricingZones.reconnect')}
            {zones.data?.missing?.length ? ` (${zones.data.missing.join(', ')})` : ''}
          </Alert>
        )}
        {!needsReconnect && served.length > 1 && served.some((location) => !zoneFor(location)) && (
          <Alert severity="info">{t('integrations.pricingZones.multiLocationNotice')}</Alert>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {zones.isLoading || locations.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : served.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('integrations.pricingZones.noLocations')}
          </Typography>
        ) : (
          served.map((location) => (
            <Box key={location.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                {location.name}
              </Typography>
              <FormControl size="small" sx={{ minWidth: 240 }} disabled={needsReconnect || save.isPending}>
                <Select
                  displayEmpty
                  value={zoneFor(location)}
                  onChange={(e) => {
                    const zoneId = String(e.target.value || '');
                    const kind = available.find((z) => z.zoneId === zoneId)?.kind || 'market';
                    save.mutate({ locationId: location.id, zoneId: zoneId || null, kind });
                  }}
                >
                  <MenuItem value="">{t('integrations.pricingZones.noZone')}</MenuItem>
                  {available.map((zone) => (
                    <MenuItem key={`${zone.kind}-${zone.zoneId}`} value={zone.zoneId}>
                      {zone.name}
                      {zone.kind === 'retail_location' ? ` — ${t('integrations.pricingZones.retailLocation')}` : ''}
                      {zone.kind === 'retail_location' && zone.priceListReason
                        ? ` (${t(`integrations.pricingZones.${PRICE_LIST_REASON_KEY[zone.priceListReason]}`)})`
                        : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          ))
        )}
        {(() => {
          const unresolved = served
            .map((location) => available.find((z) => z.zoneId === zoneFor(location)))
            .find((zone) => zone?.kind === 'retail_location' && zone.priceListReason);
          if (!unresolved?.priceListReason) return null;
          return (
            <Alert severity="warning">
              {t(`integrations.pricingZones.${PRICE_LIST_REASON_KEY[unresolved.priceListReason]}Help`)}
            </Alert>
          );
        })()}
        <Box>
          <Button
            size="small"
            onClick={() => refreshFromShopify.mutate()}
            disabled={needsReconnect || zones.isFetching || refreshFromShopify.isPending}
          >
            {t('integrations.pricingZones.refresh')}
          </Button>
        </Box>
      </Box>

      <UpgradePlansModal
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        currentPlanId={currentPlanId || 'free-preview'}
      />
    </Paper>
  );
};

export default ShopifyPricingZonesSection;
